import { useEffect, useMemo, useState } from 'react';
import {
  useApplyKfoFieldSuggestionsMutation,
  useGetKfoFieldSuggestionsQuery,
  type KfoFieldSuggestion,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Actions,
  Banner,
  DataTable,
  Dialog,
  Muted,
  QtyInput,
  SmallSelect,
  TableWrap,
  Tag,
  errorText,
  fmtDate,
  fmtNum,
  isNotFound,
  parseQty,
  qtyToInput,
} from './ui';

/** Выбор инженера по подсказке: взять ли её, в какую строку, какой объём. */
type Choice = { take: boolean; lineId: string; qty: string };

/** Единица для сравнения: без регистра, пробелов и точек, «м³» = «м3». */
function normUnit(u: string | null | undefined): string {
  return (u ?? '').toLowerCase().replace(/[\s.]/g, '').replace(/³/g, '3').replace(/²/g, '2');
}

/**
 * Единица фактов не совпадает с единицей строки КФО: объём подсказки — в
 * единице фактов, и подставить его как есть значит вписать в отчёт число не в
 * той единице (сервер в этом случае помечает подсказку неоднозначной).
 */
export function unitMismatch(s: Pick<KfoFieldSuggestion, 'unit' | 'fact_unit'>): boolean {
  const fact = normUnit(s.fact_unit);
  return fact !== '' && fact !== normUnit(s.unit);
}

/**
 * «Подставить из Прораба» (этап 2, §9): принятые факты объёма за период отчёта,
 * сопоставленные со строками КФО. Однозначные отмечены сразу, неоднозначные —
 * на выбор строки. Ничего не пишется, пока инженер не нажмёт «Подставить».
 */
export function KfoFieldSuggestionsDialog({
  reportId,
  open,
  onClose,
  onApplied,
}: {
  reportId: string;
  open: boolean;
  onClose: () => void;
  onApplied: (count: number) => void;
}) {
  const { data, isFetching, error } = useGetKfoFieldSuggestionsQuery(reportId, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });
  const [apply, { isLoading: applying }] = useApplyKfoFieldSuggestionsMutation();
  const [choices, setChoices] = useState<Record<number, Choice>>({});
  const [applyError, setApplyError] = useState<string | null>(null);
  const [showUnmatched, setShowUnmatched] = useState(false);

  const items: KfoFieldSuggestion[] = useMemo(() => data?.items ?? [], [data]);

  useEffect(() => {
    const init: Record<number, Choice> = {};
    items.forEach((s, i) => {
      init[i] = { take: s.status === 'unique', lineId: s.line_id, qty: qtyToInput(s.qty) };
    });
    setChoices(init);
    setApplyError(null);
  }, [items]);

  const taken = Object.values(choices).filter((c) => c.take);
  const invalid = taken.some((c) => parseQty(c.qty) === null || !c.lineId);
  const takenMismatch = items.filter((s, i) => choices[i]?.take && unitMismatch(s)).length;

  function update(i: number, patch: Partial<Choice>) {
    setChoices((prev) => ({ ...prev, [i]: { ...prev[i], ...patch } }));
  }

  async function onApply() {
    setApplyError(null);
    // Несколько подсказок в одну строку складываем: сервер делает upsert по
    // строке, и вторая иначе перетёрла бы первую.
    const byLine = new Map<string, { qty: number; fact_ids: string[] }>();
    items.forEach((s, i) => {
      const c = choices[i];
      if (!c?.take) return;
      const qty = parseQty(c.qty) ?? 0;
      if (qty <= 0) return;
      const acc = byLine.get(c.lineId) ?? { qty: 0, fact_ids: [] };
      acc.qty += qty;
      acc.fact_ids.push(...s.fact_ids);
      byLine.set(c.lineId, acc);
    });
    const payload = Array.from(byLine, ([line_id, v]) => ({ line_id, ...v }));
    if (payload.length === 0) {
      setApplyError('Не выбрано ни одной подсказки с объёмом больше нуля.');
      return;
    }
    if (
      takenMismatch > 0 &&
      !window.confirm(
        `В отмеченных подсказках (${takenMismatch}) единица фактов Прораба не совпадает с единицей строки КФО. ` +
          'Объём будет вписан как есть — пересчитали его в единицу строки?',
      )
    ) {
      return;
    }
    try {
      await apply({ reportId, items: payload }).unwrap();
      onApplied(payload.length);
    } catch (e) {
      setApplyError(errorText(e, 'Не удалось подставить объёмы.'));
    }
  }

  const notAvailable = isNotFound(error);

  return (
    <Dialog open={open} onClose={onClose} title="Подставить объёмы из Прораба" width={1080}>
      {isFetching && <Muted>Ищу принятые факты объёма…</Muted>}
      {notAvailable && (
        <Banner>
          Подсказки из Прораба на сервере пока не включены. Объёмы можно ввести вручную в реестре.
        </Banner>
      )}
      {error && !notAvailable && (
        <Banner $tone="danger">{errorText(error, 'Не удалось получить подсказки.')}</Banner>
      )}
      {data && (
        <>
          <Muted style={{ marginBottom: 12 }}>
            Период: {data.period_from ? fmtDate(data.period_from) : 'с начала'} —{' '}
            {data.period_to ? fmtDate(data.period_to) : '…'}. Берутся только факты, принятые
            инженером ПТО. Однозначные совпадения отмечены сразу; для неоднозначных выберите строку.
            Уже введённый объём по строке будет заменён.
          </Muted>
          {items.length === 0 ? (
            <Banner $tone="info">
              За период нет принятых фактов, которые совпали бы со строками КФО.
            </Banner>
          ) : (
            <TableWrap $maxHeight="52vh">
              <DataTable>
                <thead>
                  <tr>
                    <th></th>
                    <th>Строка КФО (Ключ · элемент)</th>
                    <th>Шифр ЕР</th>
                    <th title="Единица строки КФО; ниже — единица фактов Прораба, если другая">
                      Ед.
                    </th>
                    <th className="num" title="В единице фактов Прораба">
                      Объём
                    </th>
                    <th>Фактов</th>
                    <th>Совпадение</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s, i) => {
                    const c = choices[i];
                    if (!c) return null;
                    const options = [
                      { line_id: s.line_id, ext_key: s.ext_key, element: s.element },
                      ...s.alternatives.filter((a) => a.line_id !== s.line_id),
                    ];
                    const bad = c.take && parseQty(c.qty) === null;
                    const mismatch = unitMismatch(s);
                    return (
                      <tr
                        key={`${s.line_id}-${i}`}
                        data-tone={s.status === 'ambiguous' || mismatch ? 'warning' : undefined}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={c.take}
                            onChange={(e) => update(i, { take: e.target.checked })}
                            aria-label="Подставить"
                          />
                        </td>
                        <td>
                          {options.length > 1 ? (
                            <SmallSelect
                              value={c.lineId}
                              onChange={(e) => update(i, { lineId: e.target.value, take: true })}
                              style={{ maxWidth: 360 }}
                            >
                              {options.map((o) => (
                                <option key={o.line_id} value={o.line_id}>
                                  {o.ext_key} · {o.element ?? '—'}
                                </option>
                              ))}
                            </SmallSelect>
                          ) : (
                            <>
                              {s.ext_key} · {s.element ?? '—'}
                            </>
                          )}
                        </td>
                        <td className="nowrap">{s.ep_code}</td>
                        <td className="nowrap">
                          {s.unit}
                          {mismatch && (
                            <div>
                              <Tag
                                $tone="warning"
                                title={`Объём подсказки — в единице фактов Прораба (${s.fact_unit}), строка КФО — в ${s.unit ?? '—'}. Пересчитайте перед подстановкой.`}
                              >
                                факт: {s.fact_unit}
                              </Tag>
                            </div>
                          )}
                        </td>
                        <td className="num">
                          <QtyInput
                            value={c.qty}
                            inputMode="decimal"
                            $invalid={bad}
                            $warn={mismatch && !bad}
                            onChange={(e) => update(i, { qty: e.target.value })}
                            aria-label="Объём"
                          />
                        </td>
                        <td className="num">{s.fact_ids.length}</td>
                        <td>
                          {mismatch ? (
                            <Tag $tone="warning">единицы не совпадают</Tag>
                          ) : s.status === 'unique' ? (
                            <Tag $tone="success">однозначно</Tag>
                          ) : (
                            <Tag $tone="warning">выберите строку</Tag>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
            </TableWrap>
          )}

          {data.unmatched.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Button
                $size="sm"
                $variant="ghost"
                type="button"
                onClick={() => setShowUnmatched((v) => !v)}
              >
                {showUnmatched ? '▾' : '▸'} Без строки КФО: {data.unmatched.length}
              </Button>
              {showUnmatched && (
                <TableWrap $maxHeight="28vh" style={{ marginTop: 8 }}>
                  <DataTable>
                    <thead>
                      <tr>
                        <th>Код работ</th>
                        <th>Наименование</th>
                        <th>Чертёж</th>
                        <th className="num">Объём</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unmatched.map((u) => (
                        <tr key={u.fact_id}>
                          <td className="nowrap">{u.work_item_code}</td>
                          <td className="wrap">{u.name}</td>
                          <td className="nowrap">{u.drawing}</td>
                          <td className="num">{fmtNum(u.qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </TableWrap>
              )}
            </div>
          )}
        </>
      )}

      {takenMismatch > 0 && (
        <Banner $tone="warning" style={{ marginTop: 12 }}>
          Отмечено подсказок с другой единицей: {takenMismatch}. Объём в них — в единице фактов
          Прораба; пересчитайте его в единицу строки КФО.
        </Banner>
      )}
      {applyError && (
        <Banner $tone="danger" style={{ marginTop: 12 }}>
          {applyError}
        </Banner>
      )}
      <Actions style={{ marginTop: 16, justifyContent: 'flex-end' }}>
        <Button $variant="secondary" type="button" onClick={onClose}>
          Отмена
        </Button>
        <Button
          type="button"
          disabled={applying || taken.length === 0 || invalid || !data}
          onClick={onApply}
        >
          {applying ? 'Подставляю…' : `Подставить (${taken.length})`}
        </Button>
      </Actions>
    </Dialog>
  );
}
